// src/pages/creator-panel/notifications/NotificationsPage.js
// Notification Management Page - Table Layout (matches RemindersPage)

import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    TextField,
    Button,
    IconButton,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    FormControl,
    Select,
    MenuItem,
    CircularProgress,
    Alert,
    Snackbar,
    InputAdornment,
    Tooltip,
    Tabs,
    Tab,
    List,
    ListItemButton,
    ListItemText,
    ListItem,
    ClickAwayListener,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import NotificationsIcon from '@mui/icons-material/Notifications';
import MarkEmailReadIcon from '@mui/icons-material/MarkEmailRead';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CloseIcon from '@mui/icons-material/Close';
import axiosInstance from '../../../utils/axiosInstance';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";
const ACCENT = '#3b82f6';
const ACCENT_HOVER = '#2563eb';

const STATUS_CONFIG = {
    unread: { color: '#f59e0b', bg: '#fef3c7', label: 'Unread' },
    read: { color: '#10b981', bg: '#d1fae5', label: 'Read' },
};

const FILTER_TABS = ['all', 'unread', 'read'];

// --- Debounce Hook ---
function useDebounce(value, delay) {
    const [debounced, setDebounced] = useState(value);
    useEffect(() => {
        const timer = setTimeout(() => setDebounced(value), delay);
        return () => clearTimeout(timer);
    }, [value, delay]);
    return debounced;
}

// --- User Search Input ---
function UserSearchInput({ selectedUser, onSelectUser, onClearUser, placeholder }) {
    const [searchTerm, setSearchTerm] = useState('');
    const [results, setResults] = useState([]);
    const [isSearching, setIsSearching] = useState(false);
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const debouncedSearch = useDebounce(searchTerm, 300);

    useEffect(() => {
        if (debouncedSearch && debouncedSearch.length >= 2) {
            setIsSearching(true);
            axiosInstance.get('/api/users/team-members')
                .then((res) => {
                    let users = (res.data?.data || []).map((u) => ({
                        ...u,
                        fname: u.name?.split(' ')[0] || '',
                        lname: u.name?.split(' ').slice(1).join(' ') || '',
                    }));
                    // Client-side search filter
                    const term = debouncedSearch.toLowerCase();
                    users = users.filter((u) => {
                        const fullName = (u.name || '').toLowerCase();
                        const email = (u.email || '').toLowerCase();
                        return fullName.includes(term) || email.includes(term);
                    });
                    setResults(users);
                    setDropdownOpen(true);
                })
                .catch(() => setResults([]))
                .finally(() => setIsSearching(false));
        } else { setResults([]); setDropdownOpen(false); }
    }, [debouncedSearch]);

    const handleSelect = (user) => {
        onSelectUser(user);
        setSearchTerm('');
        setDropdownOpen(false);
    };

    if (selectedUser) {
        return (
            <Box sx={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                p: 1, borderRadius: '10px', border: '1px solid #e2e8f0', bgcolor: '#f8fafc',
            }}>
                <Typography sx={{ fontFamily, fontSize: '0.85rem', fontWeight: 500 }}>
                    {selectedUser.fname || selectedUser.first_name} {selectedUser.lname || selectedUser.last_name}
                    <Typography component="span" sx={{ fontFamily, fontSize: '0.75rem', color: '#94a3b8', ml: 0.5, textTransform: 'capitalize' }}>
                        ({selectedUser.role || 'user'})
                    </Typography>
                </Typography>
                <IconButton size="small" onClick={onClearUser} sx={{ color: '#ef4444' }}>
                    <CloseIcon fontSize="small" />
                </IconButton>
            </Box>
        );
    }

    return (
        <ClickAwayListener onClickAway={() => setDropdownOpen(false)}>
            <Box sx={{ position: 'relative' }}>
                <TextField
                    fullWidth size="small"
                    placeholder={placeholder || 'Search user...'}
                    value={searchTerm}
                    onChange={(e) => { setSearchTerm(e.target.value); setDropdownOpen(true); }}
                    sx={{ '& .MuiInputBase-root': { fontFamily, borderRadius: '10px' } }}
                    InputProps={{ endAdornment: isSearching ? <InputAdornment position="end"><CircularProgress size={16} /></InputAdornment> : null }}
                />
                {dropdownOpen && (searchTerm.length > 0 || results.length > 0) && (
                    <Paper sx={{ position: 'absolute', zIndex: 20, width: '100%', mt: 0.5, maxHeight: 200, overflow: 'auto', borderRadius: '10px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
                        <List dense disablePadding>
                            {results.length > 0 ? results.map((user) => (
                                <ListItemButton key={user.id || user.user_id} onClick={() => handleSelect(user)}>
                                    <ListItemText
                                        primary={`${user.fname || ''} ${user.lname || ''}`}
                                        secondary={user.role || ''}
                                        primaryTypographyProps={{ fontFamily, fontWeight: 500, fontSize: '0.85rem' }}
                                        secondaryTypographyProps={{ fontFamily, fontSize: '0.75rem', textTransform: 'capitalize' }}
                                    />
                                </ListItemButton>
                            )) : (!isSearching && searchTerm.length >= 2 && (
                                <ListItem><ListItemText primary="No users found." primaryTypographyProps={{ fontFamily, color: '#94a3b8', fontSize: '0.85rem' }} /></ListItem>
                            ))}
                        </List>
                    </Paper>
                )}
            </Box>
        </ClickAwayListener>
    );
}

// --- Format helpers ---
function formatDateTime(dateStr) {
    if (!dateStr) return '--';
    try {
        return new Date(dateStr).toLocaleString('en-US', {
            year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
        });
    } catch { return '--'; }
}

// --- View Notification Dialog ---
function ViewNotificationDialog({ notification, open, onClose, currentUserId }) {
    if (!notification) return null;
    const isForSelf = notification.user_id === currentUserId;
    const isFromSelf = notification.created_by_id === currentUserId;

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
            <DialogTitle sx={{ fontFamily, fontWeight: 700 }}>View Notification</DialogTitle>
            <DialogContent>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
                    <Box>
                        <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '0.85rem', color: '#1e293b', mb: 0.5 }}>Title</Typography>
                        <Typography sx={{ fontFamily, fontSize: '0.9rem', color: '#475569' }}>{notification.title}</Typography>
                    </Box>
                    {(notification.message || notification.body || notification.description) && (
                        <Box>
                            <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '0.85rem', color: '#1e293b', mb: 0.5 }}>Message</Typography>
                            <Typography sx={{ fontFamily, fontSize: '0.85rem', color: '#475569', whiteSpace: 'pre-wrap' }}>
                                {notification.message || notification.body || notification.description}
                            </Typography>
                        </Box>
                    )}
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                        <Box>
                            <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '0.85rem', color: '#1e293b', mb: 0.5 }}>Recipient</Typography>
                            <Typography sx={{ fontFamily, fontSize: '0.85rem', color: '#475569' }}>
                                {isForSelf ? 'Myself' : (notification.user_name || '--')}
                            </Typography>
                        </Box>
                        <Box>
                            <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '0.85rem', color: '#1e293b', mb: 0.5 }}>Created By</Typography>
                            <Typography sx={{ fontFamily, fontSize: '0.85rem', color: '#475569' }}>
                                {isFromSelf ? 'Myself' : (notification.creator_name || '--')}
                            </Typography>
                        </Box>
                        <Box>
                            <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '0.85rem', color: '#1e293b', mb: 0.5 }}>Status</Typography>
                            <Chip
                                label={(notification.is_read || notification.read) ? 'Read' : 'Unread'}
                                size="small"
                                sx={{
                                    fontFamily, fontWeight: 600, fontSize: '0.75rem',
                                    bgcolor: (notification.is_read || notification.read) ? STATUS_CONFIG.read.bg : STATUS_CONFIG.unread.bg,
                                    color: (notification.is_read || notification.read) ? STATUS_CONFIG.read.color : STATUS_CONFIG.unread.color,
                                }}
                            />
                        </Box>
                        <Box>
                            <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '0.85rem', color: '#1e293b', mb: 0.5 }}>Sent At</Typography>
                            <Typography sx={{ fontFamily, fontSize: '0.85rem', color: '#475569' }}>
                                {formatDateTime(notification.created_on || notification.created_at || notification.createdAt)}
                            </Typography>
                        </Box>
                    </Box>
                    {notification.read_at && (
                        <Box>
                            <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '0.85rem', color: '#1e293b', mb: 0.5 }}>Read At</Typography>
                            <Typography sx={{ fontFamily, fontSize: '0.85rem', color: ACCENT }}>
                                {formatDateTime(notification.read_at)}
                            </Typography>
                        </Box>
                    )}
                </Box>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 3 }}>
                <Button onClick={onClose} variant="contained" sx={{
                    fontFamily, fontWeight: 600, bgcolor: ACCENT, borderRadius: '10px',
                    textTransform: 'none', '&:hover': { bgcolor: ACCENT_HOVER },
                }}>
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    );
}

// --- Main Page ---
export default function NotificationsPage() {
    const [notifications, setNotifications] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [viewFilter, setViewFilter] = useState('mine');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [totalCount, setTotalCount] = useState(0);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    // Modal states
    const [viewNotification, setViewNotification] = useState(null);
    const [createDialogOpen, setCreateDialogOpen] = useState(false);
    const [createRecipient, setCreateRecipient] = useState(null);
    const [createForm, setCreateForm] = useState({ title: '', message: '' });
    const [createSubmitting, setCreateSubmitting] = useState(false);
    const [selectedFilterUser, setSelectedFilterUser] = useState(null);

    // User info
    const userStr = localStorage.getItem('user');
    const user = userStr ? JSON.parse(userStr) : null;
    const currentUserId = user?.user_id || user?.id;

    const showSnack = (message, severity = 'success') => {
        setSnackbar({ open: true, message, severity });
    };

    const fetchNotifications = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const params = {
                search,
                page: page + 1,
                limit: rowsPerPage,
                panel_type: 'creator',
            };
            if (statusFilter === 'unread') params.status_filter = 'unread';
            if (statusFilter === 'read') params.status_filter = 'read';
            if (viewFilter === 'all_users' && selectedFilterUser) {
                params.target_user_id = selectedFilterUser.id || selectedFilterUser.user_id;
            }
            if (viewFilter === 'mine') params.view_filter = 'mine';

            const response = await axiosInstance.get('/notifications', { params });
            const data = response.data;
            setNotifications(data.data || data.notifications || []);
            setTotalCount(data.total || data.totalCount || 0);
        } catch (err) {
            console.error('Failed to fetch notifications:', err);
            setError(err.response?.data?.message || 'Failed to load notifications.');
        } finally {
            setLoading(false);
        }
    }, [search, statusFilter, viewFilter, selectedFilterUser, page, rowsPerPage]);

    useEffect(() => {
        document.title = 'Notifications | Creator Panel';
        fetchNotifications();
    }, [fetchNotifications]);

    const handleViewAndMarkRead = async (notification) => {
        setViewNotification(notification);
        const notifId = notification.id || notification._id;
        const read = notification.read || notification.is_read;
        if (!read && notification.user_id === currentUserId) {
            try {
                await axiosInstance.put(`/notifications/${notifId}/read`);
                setNotifications((prev) =>
                    prev.map((n) => (n.id || n._id) === notifId ? { ...n, read: true, is_read: true } : n)
                );
            } catch (err) {
                console.error('Failed to mark as read:', err);
            }
        }
    };

    const handleMarkAsRead = async (notificationId) => {
        try {
            await axiosInstance.put(`/notifications/${notificationId}/read`);
            setNotifications((prev) =>
                prev.map((n) => (n.id || n._id) === notificationId ? { ...n, read: true, is_read: true } : n)
            );
            showSnack('Marked as read.');
        } catch (err) {
            console.error('Failed to mark as read:', err);
            showSnack(err.response?.data?.message || 'Failed to mark as read.', 'error');
        }
    };

    const handleMarkAllRead = async () => {
        try {
            await axiosInstance.put('/notifications/mark-all-read?panel_type=creator');
            setNotifications((prev) => prev.map((n) => ({ ...n, read: true, is_read: true })));
            showSnack('All notifications marked as read.');
        } catch (err) {
            console.error('Failed to mark all as read:', err);
            showSnack(err.response?.data?.message || 'Failed to mark all as read.', 'error');
        }
    };

    const handleDelete = async (notification) => {
        const notifId = notification.id || notification._id;
        if (!window.confirm(`Delete notification "${notification.title}"?`)) return;
        try {
            await axiosInstance.delete(`/notifications/${notifId}`);
            showSnack('Notification deleted!');
            fetchNotifications();
        } catch (err) {
            console.error('Failed to delete notification:', err);
            showSnack(err.response?.data?.message || 'Failed to delete notification.', 'error');
        }
    };

    const handleCreateNotification = async () => {
        if (!createForm.title.trim()) {
            showSnack('Title is required.', 'warning');
            return;
        }
        if (!createRecipient) {
            showSnack('A recipient must be selected.', 'warning');
            return;
        }
        setCreateSubmitting(true);
        try {
            await axiosInstance.post('/notifications', {
                ...createForm,
                user_id: createRecipient.id || createRecipient.user_id,
                panel_type: 'creator',
            });
            showSnack('Notification sent successfully!');
            setCreateDialogOpen(false);
            setCreateForm({ title: '', message: '' });
            setCreateRecipient(null);
            fetchNotifications();
        } catch (err) {
            console.error('Failed to create notification:', err);
            showSnack(err.response?.data?.message || 'Failed to send notification.', 'error');
        } finally {
            setCreateSubmitting(false);
        }
    };

    const handleResetFilters = () => {
        setViewFilter('mine');
        setStatusFilter('all');
        setSelectedFilterUser(null);
        setPage(0);
    };

    const getStatusChip = (notification) => {
        const read = notification.read || notification.is_read;
        const key = read ? 'read' : 'unread';
        const config = STATUS_CONFIG[key];
        return (
            <Chip label={config.label} size="small" sx={{ fontFamily, fontWeight: 600, fontSize: '0.75rem', bgcolor: config.bg, color: config.color }} />
        );
    };

    return (
        <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1400, mx: 'auto' }}>
            {/* Header */}
            <Paper
                elevation={0}
                sx={{
                    p: 2, px: 3, mb: 3, borderRadius: '16px',
                    background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                    color: '#fff',
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <NotificationsIcon sx={{ fontSize: 28 }} />
                        <Box>
                            <Typography variant="h6" sx={{ fontFamily, fontWeight: 700 }}>Notification Management</Typography>
                            <Typography variant="body2" sx={{ fontFamily, opacity: 0.9 }}>Manage notifications with user filters and read tracking</Typography>
                        </Box>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Tooltip title="Refresh">
                            <IconButton onClick={fetchNotifications} sx={{ color: '#fff' }}><RefreshIcon /></IconButton>
                        </Tooltip>
                        <Button
                            startIcon={<DoneAllIcon />}
                            variant="contained"
                            onClick={handleMarkAllRead}
                            sx={{
                                fontFamily, fontWeight: 600, bgcolor: 'rgba(255,255,255,0.2)',
                                borderRadius: '10px', textTransform: 'none',
                                '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' },
                            }}
                        >
                            Mark All Read
                        </Button>
                        <Button
                            startIcon={<AddIcon />}
                            variant="contained"
                            onClick={() => { setCreateForm({ title: '', message: '' }); setCreateRecipient(null); setCreateDialogOpen(true); }}
                            sx={{
                                fontFamily, fontWeight: 600, bgcolor: 'rgba(255,255,255,0.2)',
                                borderRadius: '10px', textTransform: 'none', px: 3,
                                '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' },
                            }}
                        >
                            Create Notification
                        </Button>
                    </Box>
                </Box>
            </Paper>

            {error && (
                <Alert severity="error" sx={{ mb: 3, borderRadius: '12px', fontFamily }}>{error}</Alert>
            )}

            {/* Filter Bar */}
            <Paper sx={{
                mb: 2, p: 2, borderRadius: '12px', border: '1px solid', borderColor: 'grey.100',
                display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap',
            }}>
                <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '0.85rem', color: '#1e293b' }}>Filters:</Typography>
                <FormControl size="small" sx={{ minWidth: 200 }}>
                    <Select
                        value={viewFilter}
                        onChange={(e) => { setViewFilter(e.target.value); setPage(0); }}
                        sx={{ fontFamily, borderRadius: '10px', fontSize: '0.85rem' }}
                    >
                        <MenuItem value="mine" sx={{ fontFamily }}>Notifications for Me</MenuItem>
                        <MenuItem value="all_users" sx={{ fontFamily }}>Notifications for Other Users</MenuItem>
                    </Select>
                </FormControl>
                <Box sx={{ width: 240, opacity: viewFilter !== 'all_users' ? 0.5 : 1, pointerEvents: viewFilter !== 'all_users' ? 'none' : 'auto' }}>
                    <UserSearchInput
                        selectedUser={selectedFilterUser}
                        onSelectUser={(u) => { setSelectedFilterUser(u); setPage(0); }}
                        onClearUser={() => { setSelectedFilterUser(null); setPage(0); }}
                        placeholder="Filter by user..."
                    />
                </Box>
                <Button onClick={handleResetFilters} sx={{ fontFamily, fontWeight: 600, color: ACCENT, textTransform: 'none', fontSize: '0.85rem' }}>
                    Reset Filters
                </Button>
            </Paper>

            <Paper
                sx={{
                    borderRadius: '16px', border: '1px solid', borderColor: 'grey.100',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.04)', overflow: 'hidden',
                }}
            >
                {/* Status Tabs */}
                <Tabs
                    value={statusFilter}
                    onChange={(e, newVal) => { setStatusFilter(newVal); setPage(0); }}
                    sx={{
                        px: 2, borderBottom: '1px solid', borderColor: 'grey.100',
                        '& .MuiTab-root': { fontFamily, fontWeight: 600, textTransform: 'none', minHeight: 48 },
                        '& .Mui-selected': { color: ACCENT },
                        '& .MuiTabs-indicator': { bgcolor: ACCENT },
                    }}
                >
                    {FILTER_TABS.map((tab) => (
                        <Tab key={tab} label={tab.charAt(0).toUpperCase() + tab.slice(1)} value={tab} />
                    ))}
                </Tabs>

                {/* Search */}
                <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 2, borderBottom: '1px solid', borderColor: 'grey.100' }}>
                    <TextField
                        placeholder="Search notifications..."
                        size="small"
                        value={search}
                        onChange={(e) => { setSearch(e.target.value); setPage(0); }}
                        sx={{ flex: 1, maxWidth: 400, '& .MuiInputBase-root': { fontFamily, borderRadius: '10px' } }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start"><SearchIcon sx={{ color: '#94a3b8' }} /></InputAdornment>
                            ),
                        }}
                    />
                    <Tooltip title="Refresh">
                        <IconButton onClick={fetchNotifications} sx={{ color: '#64748b' }}><RefreshIcon /></IconButton>
                    </Tooltip>
                </Box>

                {/* Table */}
                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow sx={{ bgcolor: '#f8fafc' }}>
                                {['Title', 'Recipient', 'Creator', 'Sent At', 'Read At', 'Status', 'Actions'].map((header) => (
                                    <TableCell key={header} sx={{
                                        fontFamily, fontWeight: 700, color: '#64748b',
                                        fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px',
                                    }}>
                                        {header}
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                <TableRow>
                                    <TableCell colSpan={7} sx={{ textAlign: 'center', py: 6 }}>
                                        <CircularProgress size={32} sx={{ color: ACCENT }} />
                                    </TableCell>
                                </TableRow>
                            ) : notifications.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={7} sx={{ textAlign: 'center', py: 6 }}>
                                        <NotificationsIcon sx={{ fontSize: 48, color: '#e2e8f0', mb: 1, display: 'block', mx: 'auto' }} />
                                        <Typography sx={{ fontFamily, color: '#94a3b8' }}>No notifications found</Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                notifications.map((notification) => {
                                    const isForSelf = notification.user_id === currentUserId;
                                    const read = notification.read || notification.is_read;
                                    return (
                                        <TableRow
                                            key={notification.id || notification._id}
                                            sx={{ '&:hover': { bgcolor: '#f8fafc' }, bgcolor: read ? 'transparent' : '#eff6ff' }}
                                        >
                                            <TableCell sx={{ fontFamily, fontWeight: read ? 500 : 700, color: '#1e293b' }}>
                                                {notification.title || '--'}
                                            </TableCell>
                                            <TableCell sx={{ fontFamily, color: '#64748b', fontSize: '0.85rem' }}>
                                                {isForSelf ? 'Myself' : (notification.user_name || '--')}
                                            </TableCell>
                                            <TableCell sx={{ fontFamily, color: '#64748b', fontSize: '0.85rem' }}>
                                                {notification.created_by_id === currentUserId ? 'Myself' : (notification.creator_name || '--')}
                                            </TableCell>
                                            <TableCell sx={{ fontFamily, color: '#64748b', fontSize: '0.85rem' }}>
                                                {formatDateTime(notification.created_on || notification.created_at || notification.createdAt)}
                                            </TableCell>
                                            <TableCell sx={{ fontFamily, color: ACCENT, fontSize: '0.85rem' }}>
                                                {notification.read_at ? formatDateTime(notification.read_at) : '--'}
                                            </TableCell>
                                            <TableCell>{getStatusChip(notification)}</TableCell>
                                            <TableCell>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                    <Tooltip title="View">
                                                        <IconButton size="small" onClick={() => handleViewAndMarkRead(notification)} sx={{ color: '#3b82f6' }}>
                                                            <VisibilityIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                    {!read && notification.user_id === currentUserId && (
                                                        <Tooltip title="Mark as read">
                                                            <IconButton size="small" onClick={() => handleMarkAsRead(notification.id || notification._id)} sx={{ color: ACCENT }}>
                                                                <MarkEmailReadIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                    )}
                                                    <Tooltip title="Delete">
                                                        <IconButton size="small" onClick={() => handleDelete(notification)} sx={{ color: '#ef4444' }}>
                                                            <DeleteIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>

                <TablePagination
                    component="div"
                    count={totalCount}
                    page={page}
                    onPageChange={(e, newPage) => setPage(newPage)}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                    sx={{ '& *': { fontFamily } }}
                />
            </Paper>

            {/* View Notification Dialog */}
            <ViewNotificationDialog
                notification={viewNotification}
                open={!!viewNotification}
                onClose={() => setViewNotification(null)}
                currentUserId={currentUserId}
            />

            {/* Create Notification Dialog */}
            <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
                <DialogTitle sx={{ fontFamily, fontWeight: 700 }}>Create New Notification</DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: 1 }}>
                        <Box>
                            <Typography sx={{ fontFamily, fontWeight: 600, fontSize: '0.85rem', color: '#1e293b', mb: 1 }}>Recipient</Typography>
                            <UserSearchInput
                                selectedUser={createRecipient}
                                onSelectUser={setCreateRecipient}
                                onClearUser={() => setCreateRecipient(null)}
                                placeholder="Search by name or email..."
                            />
                        </Box>
                        <TextField
                            label="Title"
                            fullWidth
                            value={createForm.title}
                            onChange={(e) => setCreateForm((p) => ({ ...p, title: e.target.value }))}
                            sx={{ '& .MuiInputBase-root': { fontFamily, borderRadius: '10px' }, '& .MuiInputLabel-root': { fontFamily } }}
                        />
                        <TextField
                            label="Message (Optional)"
                            fullWidth multiline rows={3}
                            value={createForm.message}
                            onChange={(e) => setCreateForm((p) => ({ ...p, message: e.target.value }))}
                            sx={{ '& .MuiInputBase-root': { fontFamily, borderRadius: '10px' }, '& .MuiInputLabel-root': { fontFamily } }}
                        />
                    </Box>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 3 }}>
                    <Button onClick={() => setCreateDialogOpen(false)} sx={{ fontFamily, fontWeight: 600, color: '#64748b', textTransform: 'none' }}>Cancel</Button>
                    <Button
                        variant="contained" onClick={handleCreateNotification}
                        disabled={createSubmitting}
                        startIcon={createSubmitting ? <CircularProgress size={18} color="inherit" /> : null}
                        sx={{ fontFamily, fontWeight: 600, bgcolor: ACCENT, borderRadius: '10px', textTransform: 'none', '&:hover': { bgcolor: ACCENT_HOVER } }}
                    >
                        {createSubmitting ? 'Sending...' : 'Send Notification'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Snackbar */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert
                    onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
                    severity={snackbar.severity}
                    sx={{ fontFamily, borderRadius: '10px' }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}
